import { SmsTarget } from "../message"
import { JobStatusUtils } from "./JobStatusUtils"
import { JobStatus } from "./job"

/**
 * What a customer can be reminded about. An addon order has its own reminder
 * text and link, but no message type of its own: it is saved as a service
 * proposal reminder, so it is told apart here.
 */
export enum JobReminderType {
  COMPLETED_BIKE = "COMPLETED_BIKE",
  SERVICE_PROPOSAL = "SERVICE_PROPOSAL",
  ADDON_ORDER = "ADDON_ORDER",
}

export interface JobReminder {
  type: JobReminderType
  /** When the job started waiting on the customer: finished, proposal sent or addon order sent. */
  waitingSince: Date | null
  /** The latest reminder sent since then, if any. */
  lastReminderSentAt: Date | null
  /**
   * Whether a mechanic may send it with Påminn kund. Not while the automatic
   * pickup reminders are reminding the customer about a finished bike.
   */
  canSendManually: boolean
  /**
   * The automatic reminders about a proposal or addon order are used up and
   * the customer still hasn't answered, so the workshop should call.
   */
  automaticRemindersUsedUp: boolean
}

export interface JobReminderInput {
  status: JobStatus
  hasPendingAddonOrder: boolean
  finishedAt: Date | null
  proposalSentAt: Date | null
  lastAddonOrderSentAt: Date | null
  lastCompletedReminderSentAt: Date | null
  lastProposalReminderSentAt: Date | null
  pickupRemindersActive: boolean
  proposalReminderLimitReachedAt: Date | null
}

// A reminder sent before the job started waiting again (e.g. a proposal
// reminder, then an addon order) is about something the customer already
// answered, so it doesn't count.
const reminderSince = (
  lastReminderSentAt: Date | null,
  waitingSince: Date | null,
): Date | null => {
  if (!lastReminderSentAt) return null
  if (!waitingSince) return lastReminderSentAt
  return new Date(lastReminderSentAt).getTime() >=
    new Date(waitingSince).getTime()
    ? lastReminderSentAt
    : null
}

export const getJobReminder = (input: JobReminderInput): JobReminder | null => {
  if (input.status === JobStatus.COMPLETED) {
    return {
      type: JobReminderType.COMPLETED_BIKE,
      waitingSince: input.finishedAt,
      lastReminderSentAt: reminderSince(
        input.lastCompletedReminderSentAt,
        input.finishedAt,
      ),
      canSendManually: !input.pickupRemindersActive,
      automaticRemindersUsedUp: false,
    }
  }

  if (input.status === JobStatus.SERVICE_PROPOSAL_SENT) {
    return {
      type: JobReminderType.SERVICE_PROPOSAL,
      waitingSince: input.proposalSentAt,
      lastReminderSentAt: reminderSince(
        input.lastProposalReminderSentAt,
        input.proposalSentAt,
      ),
      canSendManually: true,
      automaticRemindersUsedUp: input.proposalReminderLimitReachedAt !== null,
    }
  }

  // Stale pending addon order rows outlive a finished job, so only a job that
  // is still being worked on is waiting for the answer.
  if (
    input.hasPendingAddonOrder &&
    !JobStatusUtils.isStatusAtOrPast(input.status, JobStatus.COMPLETED)
  ) {
    return {
      type: JobReminderType.ADDON_ORDER,
      waitingSince: input.lastAddonOrderSentAt,
      lastReminderSentAt: reminderSince(
        input.lastProposalReminderSentAt,
        input.lastAddonOrderSentAt,
      ),
      canSendManually: true,
      automaticRemindersUsedUp: input.proposalReminderLimitReachedAt !== null,
    }
  }

  return null
}

export const toReminderSmsTarget = (type: JobReminderType): SmsTarget => {
  switch (type) {
    case JobReminderType.COMPLETED_BIKE:
      return SmsTarget.CHAT
    case JobReminderType.SERVICE_PROPOSAL:
      return SmsTarget.SERVICE_PROPOSAL_REMINDER
    case JobReminderType.ADDON_ORDER:
      return SmsTarget.ADDON_ORDER_REMINDER
  }
}
